import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, Gift, Copy, Check } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { shopApi } from "@/lib/api";
import { toast } from "sonner";

const SLICES = [
  { label: "5% off", color: "#f5c518" },
  { label: "10% off", color: "#0b0b0b" },
  { label: "15% off", color: "#f5c518" },
  { label: "20% off", color: "#0b0b0b" },
  { label: "Free ship", color: "#f5c518" },
  { label: "10% off", color: "#0b0b0b" },
  { label: "5% off", color: "#f5c518" },
  { label: "15% off", color: "#0b0b0b" },
];

const SEEN_KEY = "vm_spin_seen";
const CODE_KEY = "vm_spin_code";

export default function SpinWheel() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null); // { code, label, percent_off }
  const [rotation, setRotation] = useState(0);
  const [copied, setCopied] = useState(false);
  const shownRef = useRef(false);

  // Auto-open once per browser after 12s OR on first exit-intent, whichever comes first
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem(SEEN_KEY)) return;
    if (shownRef.current) return;

    const show = () => {
      if (shownRef.current || localStorage.getItem(SEEN_KEY)) return;
      shownRef.current = true;
      // Lock the legacy ExitIntent modal so both never open together
      try { localStorage.setItem("novalynx_exit_intent_v1", "shown"); } catch { /* ignore */ }
      setOpen(true);
    };
    const timer = setTimeout(show, 12000);
    const onLeave = (e) => { if (e.clientY <= 0) show(); };
    document.addEventListener("mouseleave", onLeave);
    return () => { clearTimeout(timer); document.removeEventListener("mouseleave", onLeave); };
  }, []);

  const dismiss = () => {
    setOpen(false);
    localStorage.setItem(SEEN_KEY, "1");
    // Also mark the legacy ExitIntentModal as seen so shoppers never get double-popped
    try { localStorage.setItem("novalynx_exit_intent_v1", "shown"); } catch { /* ignore */ }
  };

  const spin = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid email"); return;
    }
    setSpinning(true);
    try {
      const r = await shopApi.spin(email);
      // Find slice index matching prize label; fall back to first slice
      const targetIdx = SLICES.findIndex(
        (s) => s.label.toLowerCase().replace(/[^a-z0-9]/g, "") === (r.label || "").toLowerCase().replace(/[^a-z0-9]/g, "")
      );
      const idx = targetIdx >= 0 ? targetIdx : 0;
      const perSlice = 360 / SLICES.length;
      // Land pointer at the top on the middle of the target slice, +6 full spins for drama
      const finalRotation = 360 * 6 - (idx * perSlice + perSlice / 2);
      setRotation(finalRotation);
      setTimeout(() => {
        setResult(r);
        localStorage.setItem(SEEN_KEY, "1");
        localStorage.setItem(CODE_KEY, r.code);
        try { localStorage.setItem("novalynx_exit_intent_v1", "shown"); } catch { /* ignore */ }
        setSpinning(false);
      }, 4200); // matches CSS transition
    } catch (e) {
      toast.error(e.response?.data?.detail || "Spin failed");
      setSpinning(false);
    }
  };

  const copyCode = () => {
    if (!result?.code) return;
    navigator.clipboard.writeText(result.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const perSlice = 360 / SLICES.length;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && dismiss()}>
      <DialogContent className="bg-black border-primary/40 max-w-md text-center overflow-hidden" data-testid="spin-modal">
        <button onClick={dismiss} className="absolute right-3 top-3 text-white/40 hover:text-white z-20" aria-label="Dismiss">
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-center justify-center gap-2 text-primary text-xs uppercase tracking-widest mb-1">
          <Gift className="h-4 w-4" fill="currentColor" /> One-time offer
        </div>
        <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight mb-4">
          {result ? "🎉 You won " + result.label + "!" : "Spin to win up to 20% off"}
        </h2>

        {/* Wheel */}
        <div className="relative w-64 h-64 mx-auto mb-5">
          {/* Pointer */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1 z-10">
            <div className="w-0 h-0 border-l-[10px] border-r-[10px] border-t-[16px] border-l-transparent border-r-transparent border-t-primary drop-shadow" />
          </div>
          <motion.svg
            viewBox="0 0 200 200"
            className="w-full h-full"
            animate={{ rotate: rotation }}
            transition={{ duration: 4, ease: [0.16, 1, 0.3, 1] }}
            style={{ transformOrigin: "50% 50%" }}
          >
            {SLICES.map((s, i) => {
              const startAngle = i * perSlice - 90;
              const endAngle = (i + 1) * perSlice - 90;
              const startRad = (startAngle * Math.PI) / 180;
              const endRad = (endAngle * Math.PI) / 180;
              const x1 = 100 + 96 * Math.cos(startRad);
              const y1 = 100 + 96 * Math.sin(startRad);
              const x2 = 100 + 96 * Math.cos(endRad);
              const y2 = 100 + 96 * Math.sin(endRad);
              const largeArc = perSlice > 180 ? 1 : 0;
              const midAngle = ((startAngle + endAngle) / 2) * (Math.PI / 180);
              const tx = 100 + 62 * Math.cos(midAngle);
              const ty = 100 + 62 * Math.sin(midAngle);
              return (
                <g key={i}>
                  <path
                    d={`M100,100 L${x1},${y1} A96,96 0 ${largeArc} 1 ${x2},${y2} Z`}
                    fill={s.color}
                    stroke="rgba(0,0,0,0.4)"
                    strokeWidth="0.5"
                  />
                  <text
                    x={tx}
                    y={ty}
                    fill={s.color === "#f5c518" ? "#000" : "#f5c518"}
                    fontSize="10"
                    fontWeight="700"
                    textAnchor="middle"
                    alignmentBaseline="middle"
                    transform={`rotate(${((startAngle + endAngle) / 2) + 90} ${tx} ${ty})`}
                  >
                    {s.label}
                  </text>
                </g>
              );
            })}
            <circle cx="100" cy="100" r="14" fill="#f5c518" stroke="#000" strokeWidth="2" />
          </motion.svg>
        </div>

        {!result ? (
          <>
            <p className="text-sm text-muted-foreground mb-3">Drop your email and spin. One code per visitor, valid 3 days.</p>
            <div className="flex flex-col gap-2">
              <Input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={spinning}
                data-testid="spin-email"
                className="text-center"
              />
              <Button className="btn-primary" onClick={spin} disabled={spinning} data-testid="spin-go">
                {spinning ? "Spinning…" : "Spin the wheel"}
              </Button>
              <button onClick={dismiss} className="text-xs text-muted-foreground hover:text-white mt-1">No thanks, I'll pay full price</button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-3">By entering your email you agree to receive occasional offers. Unsubscribe anytime.</p>
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-3">Your code:</p>
            <button
              onClick={copyCode}
              className="w-full py-3 px-4 rounded-xl bg-primary/10 border border-primary/40 font-mono text-xl tracking-widest text-primary flex items-center justify-center gap-3 hover:bg-primary/20 transition"
              data-testid="spin-code"
            >
              {result.code}
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </button>
            <p className="text-xs text-muted-foreground mt-3">Valid 3 days · single use · applies at checkout</p>
            <a href="/shop" className="inline-block mt-4">
              <Button className="btn-primary" data-testid="spin-shop">Start shopping →</Button>
            </a>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
